import React from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, Container, Grid, Image, Typography } from "@iho-design-system/Loader";

import BankBanner from "../components/finance/bank/Banner";
import BankList from "../components/finance/bank/List";
import BankToolbar from "../components/finance/bank/Toolbar";
import FinanceTab from "../components/finance/Tab";
import ReportBanner from "../components/finance/report/Banner";
import ReportList from "../components/finance/report/List";
import ReportToolbar from "../components/finance/report/Toolbar";
import SettlementBanner from "../components/finance/settlement/Banner";
import SettlementList from "../components/finance/settlement/List";
import SettlementToolbar from "../components/finance/settlement/Toolbar";

import LoadingCustomSVG from "../assets/images/icons/loading-custom.svg";
import getAPI from "../api/get";

const Finance = () => {
  const location = useLocation();
  const [financeData, setFinanceData] = React.useState({
    bankAccount: "",
    walletBalance: "",
    walletReport: "",
    GetPartialBalance: "",
    balance: "",
    creditPayments: "",
    walletRequests: "",
  });

  const [searchParameters, setSearchParameters] = React.useState({
    transType: "-1",
    docType: "2",
    startDate: "",
    endDate: "",
  });

  const [insufficientStatus, setInsufficientStatus] = React.useState(false);
  const [reloadFinance, setReloadFinance] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [alert, setAlert] = React.useState("");
  const [alertColor, setAlertColor] = React.useState("success");
  const [alertStatus, setAlertStatus] = React.useState("d-none");

  const alertProps = ([color, message]) => {
    setAlert(message);
    setAlertColor(color);
    setAlertStatus("d-flex");
    setTimeout(() => setAlertStatus("d-none"), 2500);
  };

  const reloadFinanceProps = (value) => setReloadFinance(value);
  const insufficientStatusProps = (status) => setInsufficientStatus(status);

  React.useEffect(() => {
    const fetchData = async () => {
      if (location.pathname === "/finance/bank" || location.pathname === "/finance/report") {
        const bankAccount = await getAPI({
          ctrl: "BankAccount",
          method: "GetAll",
          query: "",
        });
        setFinanceData((prev) => ({ ...prev, bankAccount }));
      }

      if (location.pathname === "/finance/report") {
        const [balance, walletBalance, walletReport, walletRequests] = await Promise.all([
          getAPI({ ctrl: "Financial", method: "GetAllBalances", query: "" }),
          getAPI({
            ctrl: "UserProfile",
            method: "GetWalletBalanceEx",
            query: "",
          }),
          getAPI({
            ctrl: "UserProfile",
            method: "WalletReport",
            query:
              "?PageIndex=1&PageSize=10&TransType=-1&DocType=2&StartDate=",
          }),
          getAPI({
            ctrl: "UserProfile",
            method: "GetWalletRequests",
            query: "",
          }),
        ]);

        setFinanceData((prev) => ({
          ...prev,
          balance,
          walletBalance,
          walletReport,
          walletRequests,
        }));
      }

      if (location.pathname === "/finance/settlement") {
        const balance = await getAPI({
          ctrl: "Financial",
          method: "GetAllBalances",
          query: "",
        });
        setFinanceData((prev) => ({ ...prev, balance }));
      }
    };

    fetchData();
  }, [location.pathname, reloadFinance]);

  const searchProps = async (params) => {
    if (params.section === "report") {
      setIsLoading(true);
      const walletReport = await getAPI({
        ctrl: "UserProfile",
        method: "WalletReport",
        query: `?PageIndex=1&PageSize=10&TransType=${params.transType}&DocType=${params.docType}&StartDate=${params.startDate}&EndDate=${params.endDate}`,
      });
      setSearchParameters(params);
      setFinanceData((prev) => ({ ...prev, walletReport }));
      setIsLoading(false);
    }

    if (params.section === "settlement") {
      setSearchParameters(params);
    }
  };

  const paginationProps = async (step) => {
    const report = financeData.walletReport;
    if (!report) return;

    const { PageIndex, TotalItems, PageSize } = report.data;
    const totalPages = Math.ceil(TotalItems / PageSize);
    const nextPage = PageIndex + step;

    if (nextPage < 1 || nextPage > totalPages) return;

    const walletReport = await getAPI({
      ctrl: "UserProfile",
      method: "WalletReport",
      query: `?PageIndex=${nextPage}&PageSize=10&TransType=${searchParameters.transType}&DocType=${searchParameters.docType}&StartDate=${searchParameters.startDate}&EndDate=${searchParameters.endDate}`,
    });

    setFinanceData((prev) => ({ ...prev, walletReport }));
  };

  const pageTab =
    location.pathname === "/finance/report"
      ? "report"
      : location.pathname === "/finance/settlement"
      ? "settlement"
      : "bank";

  const pendingRequest = Array.isArray(financeData.walletRequests?.data)
    ? financeData.walletRequests.data[0]
    : null;
  const pendingStatus = pendingRequest?.StatusCode;
  const pendingDate = pendingRequest?.DueDate
    ? new Date(pendingRequest.DueDate).toLocaleDateString("fa-IR")
    : "";

  const walletSettlementPendingBanner = pendingRequest ? (
    <Grid.Item size="col-12">
      <Box
        className="w-100 d-flex align-item-center mb-lg"
        ms={{
          backgroundColor: "#fff5e7",
          borderRight: "4px solid #ffa500",
          gap: "8px",
          padding: "12px",
        }}
      >
        <Typography
          ms={{
            color: "#E65200",
          }}
        >
          {pendingStatus == 1 ? (
            "درخواست تسویه کیف پول شما در حال بررسی است"
          ) : (
            <>
              تسویه کیف پول و واریز به حساب شما در تاریخ{" "}
              <Box
                component="span"
                ms={{
                  fontWeight: "bold",
                }}
              >
                {pendingDate}
              </Box>{" "}
              انجام خواهد شد
            </>
          )}
        </Typography>
      </Box>
    </Grid.Item>
  ) : null;

  return (
    <Container size="lg">
      <Grid>
        <Grid.Wrapper>
          <Grid.Item size="col-12">
            <FinanceTab />
          </Grid.Item>

          {walletSettlementPendingBanner}

          <Grid.Item className="pr-0 pl-0" size="col-12">
            {pageTab === "report" && (
              <ReportBanner
                props={{
                  walletBalance: financeData.walletBalance,
                  bankAccount: financeData.bankAccount,
                  balance: financeData.balance,
                }}
                alertProps={alertProps}
                reloadFinanceProps={reloadFinanceProps}
              />
            )}

            {pageTab === "settlement" && (
              <SettlementBanner
                props={{
                  balance: financeData.balance,
                  insufficientStatus,
                }}
                alertProps={alertProps}
                reloadFinanceProps={reloadFinanceProps}
              />
            )}

            {pageTab === "bank" && (
              <BankBanner props={{ bankAccount: financeData.bankAccount }} />
            )}
          </Grid.Item>

          <Grid.Item className="pr-0 pl-0" size="col-12">
            {pageTab === "report" && (
              <ReportToolbar searchProps={searchProps} />
            )}
            {pageTab === "settlement" && (
              <SettlementToolbar searchProps={searchProps} />
            )}
            {pageTab === "bank" && (
              <BankToolbar
                props={{ bankAccount: financeData.bankAccount }}
                alertProps={alertProps}
                reloadFinanceProps={reloadFinanceProps}
              />
            )}
          </Grid.Item>

          <Grid.Item className="pr-0 pl-0" size="col-12">
            {pageTab !== "bank" && isLoading && (
              <Image
                className="d-block mx-auto"
                src={LoadingCustomSVG}
                ms={{ width: 40, height: 40 }}
              />
            )}

            {pageTab === "report" && !isLoading && (
              <ReportList
                props={{ walletReport: financeData.walletReport }}
                paginationProps={paginationProps}
              />
            )}

            {pageTab === "settlement" && !isLoading && (
              <SettlementList
                props={{
                  creditPayments: financeData.creditPayments,
                  balance: financeData.balance,
                  serviceType: searchParameters.serviceType || "1",
                  startDate: searchParameters.startDate || "",
                  endDate: searchParameters.endDate || "",
                  GetPartialBalance: financeData.GetPartialBalance,
                }}
                insufficientStatusProps={insufficientStatusProps}
                alertProps={alertProps}
                reloadFinanceProps={reloadFinanceProps}
              />
            )}

            {pageTab === "bank" && (
              <BankList
                props={{ bankAccount: financeData.bankAccount }}
                alertProps={alertProps}
                reloadFinanceProps={reloadFinanceProps}
              />
            )}
          </Grid.Item>
        </Grid.Wrapper>
      </Grid>
    </Container>
  );
};

export default Finance;
